/**
 * EmptyState — the list loaded and has nothing in it.
 *
 * Only for a *successful* empty answer. A failed load is `ErrorState`
 * (`QueryState` picks for you): "No matters yet" on a 500 tells the user
 * something false.
 *
 *     <EmptyState
 *       icon={Inbox}
 *       title="No matters yet"
 *       body="Open a matter to keep its documents, tasks and hearings together."
 *       action={<Button>New matter</Button>}
 *     />
 *
 * When a filter or search produced the empty list, say so and offer to clear
 * it (`filtered`): "No matters match these filters", not "No matters yet".
 *
 * `hint` is the old name of `body` (components/todo/primitives.tsx), kept so
 * existing callers compile unchanged.
 */
import { CheckCircle2 } from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';

import { cn } from './cn';

export interface EmptyStateProps {
  title: ReactNode;
  body?: ReactNode;
  /** @deprecated use `body`. */
  hint?: ReactNode;
  icon?: ComponentType<{ size?: number; strokeWidth?: number; className?: string; 'aria-hidden'?: boolean }>;
  action?: ReactNode;
  /** The list is empty because of a filter or search, not because nothing exists. */
  filtered?: boolean;
  compact?: boolean;
  className?: string;
}

export function EmptyState({
  title,
  body,
  hint,
  icon: Icon = CheckCircle2,
  action,
  filtered = false,
  compact = false,
  className,
}: EmptyStateProps) {
  const text = body ?? hint;
  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-xl border border-dashed border-slate-200 bg-slate-50/60 text-center',
        compact ? 'gap-1 px-3 py-4' : 'gap-2 px-4 py-8',
        className,
      )}
      data-empty-state={filtered ? 'filtered' : 'empty'}
      role="status"
    >
      <Icon aria-hidden className="text-gold" size={compact ? 16 : 20} strokeWidth={1.5} />
      <p className={cn('font-medium text-fg', compact ? 'text-sm' : 'text-base')}>{title}</p>
      {text ? <p className="max-w-sm text-sm text-fg-muted">{text}</p> : null}
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  );
}
